import { isReferenceMonth } from '@shared/date/reference-month'
import type { ReferenceMonth } from '@shared/date/reference-month'
import { isValidCents } from '@shared/money/cents'
import { NO_FILTERS } from './filters'
import type { PaymentMethod, TransactionFilters } from './filters'

/**
 * A busca da URL de `/transacoes`, nos dois sentidos, **num arquivo só**.
 *
 * ## Por que os dois sentidos moram juntos
 *
 * Porque separá-los já custou um bug. `validateSearch` é uma **allowlist**: o
 * que ela não devolve, a tela nunca vê. Quando `?sem_categoria=1` entrou, o
 * filtro funcionava pelo botão — que navega com a busca já montada — e **não**
 * ao colar a URL, porque o validador da rota, que morava em `router.tsx`, só
 * conhecia `mes` (decisão 142e). Com um lado em cada arquivo, acrescentar um
 * filtro é lembrar de dois lugares; com os dois aqui, é lembrar de um, e o
 * teste de ida-e-volta reprova quando alguém lembra pela metade.
 *
 * ## O que chega é `unknown` de verdade
 *
 * Digitado, colado, ou de um link antigo. **Valor estranho é descartado, não
 * recusado** — derrubar a aba mais visitada do app por um caractere colado
 * errado seria a resposta errada, e um filtro que não se entende não pode
 * esconder transação nenhuma. Mesmo critério do `mes` e do `destino` de
 * `/entrar`.
 *
 * ## E o TanStack faz parse tipo JSON de cada valor
 *
 * `?sem_categoria=1` chega como **número** `1`, não `'1'`; `?min=5000` chega
 * como `5000`. Isto foi medido, não suposto — a primeira versão comparava com
 * `'1'` e o filtro não ligava ao colar a URL. Cada leitor abaixo aceita as duas
 * formas e normaliza para uma só.
 *
 * **E na volta ele CITA a string que voltaria como número.** Medido: com
 * `sem_categoria: '1'`, o link que o app gera é `?sem_categoria=%221%22` — o
 * TanStack precisa das aspas para o valor não voltar como `1`. Já
 * `?mes=2026-08` e `?busca=padaria` saem limpos, porque nenhuma das duas strings
 * é ambígua. Por isso o flag é o **número 1**, e não a string: assim o link que
 * a pessoa compartilha é o mesmo que ela digitaria.
 *
 * ## Dinheiro na URL vai em CENTAVOS
 *
 * `?min=5000` é R$ 50,00. Inteiro, como manda a decisão 9 — e assim não há
 * ponto decimal para alguém parsear, nem `50.5` virando 50 centavos numa
 * localidade diferente. Quem mostra reais é o formulário, que já tem
 * `parseAmountInput`/`centsToInput` para isso.
 */

const PAYMENT_METHODS = ['pix', 'debit', 'credit', 'boleto', 'transfer', 'cash', 'other'] as const

/**
 * **A lista acima é de runtime.** Mesma armadilha de `CASH_IMPACTS`: um método
 * que exista no tipo e falte aqui vira um filtro que a URL nunca aceita, em
 * silêncio. A linha abaixo só compila se os dois conjuntos forem iguais.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _metodosCobremAUniao: Equals<PaymentMethod, (typeof PAYMENT_METHODS)[number]> = true
void _metodosCobremAUniao

/**
 * A busca **depois** da allowlist. Todo campo é opcional, e ausente quer dizer
 * "não filtrado" — ver `searchFor`, que omite em vez de zerar.
 */
export type TransacoesSearch = {
  mes?: ReferenceMonth
  sem_categoria?: 1
  /** A fila de conferência da Fase 10. */
  auto?: 1
  busca?: string
  categoria?: string
  instrumento?: string
  metodo?: PaymentMethod
  /** Centavos inteiros, em módulo. */
  min?: number
  max?: number
}

/** Texto não vazio, ou `undefined`. Espaço em branco não é filtro. */
function texto(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * Centavos vindos da URL. **Negativo é descartado**: a faixa é em módulo (ver
 * `filters.ts`), e um `?min=-5000` que passasse faria a comparação de grandeza
 * aceitar tudo, que é o mesmo que filtro nenhum — só que sem a pessoa saber.
 */
function centavos(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value
  if (!isValidCents(n) || n < 0) return undefined
  return n
}

function metodo(value: unknown): PaymentMethod | undefined {
  return PAYMENT_METHODS.find((m) => m === value)
}

/**
 * A allowlist da rota. **É esta função que `validateSearch` chama** — não uma
 * cópia dela escrita em `router.tsx`.
 */
export function normalizeSearch(search: Record<string, unknown>): TransacoesSearch {
  const mes = search['mes']
  const bruto = search['sem_categoria']

  return {
    ...(isReferenceMonth(mes) ? { mes } : {}),
    ...(bruto === 1 || bruto === '1' ? ({ sem_categoria: 1 } as const) : {}),
    ...(search['auto'] === 1 || search['auto'] === '1' ? ({ auto: 1 } as const) : {}),
    ...(texto(search['busca']) === undefined ? {} : { busca: texto(search['busca']) as string }),
    ...(texto(search['categoria']) === undefined
      ? {}
      : { categoria: texto(search['categoria']) as string }),
    ...(texto(search['instrumento']) === undefined
      ? {}
      : { instrumento: texto(search['instrumento']) as string }),
    ...(metodo(search['metodo']) === undefined ? {} : { metodo: metodo(search['metodo']) }),
    ...(centavos(search['min']) === undefined ? {} : { min: centavos(search['min']) }),
    ...(centavos(search['max']) === undefined ? {} : { max: centavos(search['max']) }),
  }
}

/** URL → filtros. O que a tela lê. */
export function filtersFrom(search: TransacoesSearch): TransactionFilters {
  return {
    ...NO_FILTERS,
    q: search.busca ?? '',
    categoryId: search.categoria ?? null,
    instrumentId: search.instrumento ?? null,
    method: search.metodo ?? null,
    minCents: search.min ?? null,
    maxCents: search.max ?? null,
    onlyUncategorized: search.sem_categoria === 1,
    onlyAuto: search.auto === 1,
  }
}

/**
 * Filtros → URL. **Filtro desligado SAI da URL em vez de virar `=0` ou `=`.**
 *
 * Link limpo é o que a pessoa compartilha, e um parâmetro que só existe quando
 * vale alguma coisa não acumula lixo no histórico do navegador — o botão
 * "voltar" atravessaria uma URL por filtro desligado.
 */
export function searchFor(
  month: ReferenceMonth,
  filters: TransactionFilters,
): TransacoesSearch {
  const q = filters.q.trim()
  return {
    mes: month,
    ...(filters.onlyUncategorized ? ({ sem_categoria: 1 } as const) : {}),
    ...(filters.onlyAuto ? ({ auto: 1 } as const) : {}),
    ...(q === '' ? {} : { busca: q }),
    ...(filters.categoryId === null ? {} : { categoria: filters.categoryId }),
    ...(filters.instrumentId === null ? {} : { instrumento: filters.instrumentId }),
    ...(filters.method === null ? {} : { metodo: filters.method }),
    ...(filters.minCents === null ? {} : { min: filters.minCents }),
    ...(filters.maxCents === null ? {} : { max: filters.maxCents }),
  }
}
